import { useQueryClient, useQueryErrorResetBoundary } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import type { FallbackProps } from 'react-error-boundary'

import { Loading } from '../../ui/miscellaneous/loading'

interface InvalidTokenRetryFallbackProps extends FallbackProps {
  retryCount: number
  maxRetries: number
  onRetryAttempt: () => void
  retryDelay: number
}

/**
 * To be used as a fallback when an 'invalid token' error is encountered.
 * This will clear the query client and reset the error boundary.
 */
export function InvalidTokenRetryFallback({
  error,
  resetErrorBoundary,
  retryCount,
  maxRetries,
  onRetryAttempt,
  retryDelay = 1000,
}: InvalidTokenRetryFallbackProps) {
  const queryClient = useQueryClient()
  const { reset: resetQueryErrorBoundary } = useQueryErrorResetBoundary()
  const hasHandledInvalidTokenRef = useRef(false)
  const errorMessage = error instanceof Error ? error.message : String(error)
  const isInvalidTokenError = errorMessage === 'Invalid token'
  const hasReachedRetryLimit = retryCount >= maxRetries

  useEffect(() => {
    if (!isInvalidTokenError) return
    if (hasReachedRetryLimit) return
    if (hasHandledInvalidTokenRef.current) return

    hasHandledInvalidTokenRef.current = true
    onRetryAttempt()

    const resetTimeoutId = window.setTimeout(() => {
      queryClient.clear()
      resetErrorBoundary()
      resetQueryErrorBoundary()
    }, retryDelay)

    return () => {
      window.clearTimeout(resetTimeoutId)
    }
  }, [
    isInvalidTokenError,
    hasReachedRetryLimit,
    onRetryAttempt,
    queryClient,
    resetErrorBoundary,
    resetQueryErrorBoundary,
    retryDelay,
  ])

  if (!isInvalidTokenError || hasReachedRetryLimit) {
    throw error
  }

  return <Loading style="fullscreen" />
}
